import { displayTitle, type AskAnswer } from '@buakei/types';
import { Button, EmptyState, ErrorState, LoadingState, Sheet, cn } from '@buakei/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FileText, History, Plus, Send, Trash2 } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ReportProblem } from './ReportProblem';
import { SaveMemo } from './SaveMemo';
import { Link, useLocation, useSearchParams } from 'react-router';
import type { IdentityClient } from '@/auth/identity-client';
import { useHeaderSlot } from '@/shell/header-slot';
import { ExternalResearchLogView, ExternalSourceItem } from '@/assistant/ExternalSources';
import { assistantApi } from '@/services/api';
import {
  QuestionDocumentChips,
  QuestionDocumentsPanel,
  useDocumentUploads,
} from './QuestionDocuments';

/**
 * Ask Buakei over the Acts (ADR-017): live answers drawn only from Acts in the library. Each
 * numbered source is a real passage, with the quoted text and a link to the original
 * document at that page. Questions are not stored; the server allows a few per day.
 */

/** A question shown in the conversation; `chatId` is the chat it was asked in, if any. */
/** `documents`: research documents read for this question (ADR-045). */
type Turn = { id: number; question: string; chatId?: string | null; documents?: string[] };

const QUESTION_MAX = 1000;

function pages(start: number, end: number) {
  return start === end ? `page ${start}` : `pages ${start}–${end}`;
}

type Part = string | { source: number };
type Line = { kind: 'paragraph' | 'point' | 'heading'; label: string | null; parts: Part[] };

/**
 * Lay the answer out as the model was asked to write it: an opening answer, points on lines
 * starting "- " with a short label before a colon, and a closing summary. Citation numbers
 * stay attached to the text they support.
 */
export function layoutAnswer(segments: AskAnswer['segments']): Line[] {
  const lines: Part[][] = [[]];
  for (const segment of segments) {
    const pieces = segment.text.replace(/\*\*/g, '').split('\n');
    pieces.forEach((piece, i) => {
      if (i > 0) lines.push([]);
      if (piece) lines.at(-1)!.push(piece);
    });
    for (const n of segment.sources) lines.at(-1)!.push({ source: n });
  }
  const out: Line[] = [];
  for (const parts of lines) {
    const first = parts[0];
    if (parts.length === 0 || (parts.length === 1 && typeof first === 'string' && !first.trim()))
      continue;
    // A section heading alone on its line, e.g. "Facts:" in a case brief (ADR-020).
    if (
      parts.length === 1 &&
      typeof first === 'string' &&
      /^\s*[A-Z][A-Za-z ()'-]{1,40}:\s*$/.test(first)
    ) {
      out.push({ kind: 'heading', label: first.trim().replace(/:$/, ''), parts: [] });
      continue;
    }
    if (typeof first === 'string' && /^\s*[-•]\s+/.test(first)) {
      const text = first.replace(/^\s*[-•]\s+/, '');
      const labelled = /^([^:]{1,60}):\s*/.exec(text);
      out.push({
        kind: 'point',
        label: labelled ? labelled[1]!.trim() : null,
        parts: [labelled ? text.slice(labelled[0].length) : text, ...parts.slice(1)],
      });
    } else {
      out.push({ kind: 'paragraph', label: null, parts });
    }
  }
  return out;
}

/** Case names arrive as *Name v Name*: shown in italics, the legal convention; stray asterisks go. */
export function italicise(text: string): ReactNode[] {
  return text
    .split(/\*([^*\n]+)\*/g)
    .map((piece, i) => (i % 2 === 1 ? <em key={i}>{piece}</em> : piece.replace(/\*/g, '')));
}

function AnswerBody({ answer, anchor }: { answer: AskAnswer; anchor: (n: number) => string }) {
  const lines = layoutAnswer(answer.segments);
  const render = (parts: Part[]) =>
    parts.map((part, i) =>
      typeof part === 'string' ? (
        <span key={i}>
          {/* Text after a citation number starts a new sentence: keep the space before it. */}
          {i > 0 && typeof parts[i - 1] !== 'string' && /^[^\s.,;:)]/.test(part) ? ' ' : ''}
          {italicise(part)}
        </span>
      ) : (
        <a
          key={i}
          href={`#${anchor(part.source)}`}
          className="ml-0.5 align-super text-xs font-semibold text-navy-900 underline"
          aria-label={`Source ${part.source}`}
        >
          [{part.source}]
        </a>
      ),
    );
  // Consecutive points form one list.
  const blocks: (Line | Line[])[] = [];
  for (const line of lines) {
    const last = blocks.at(-1);
    if (line.kind === 'point' && Array.isArray(last)) last.push(line);
    else blocks.push(line.kind === 'point' ? [line] : line);
  }
  return (
    <div className="flex flex-col gap-3 leading-relaxed text-ink-900">
      {blocks.map((block, i) =>
        Array.isArray(block) ? (
          <ul key={i} className="flex list-disc flex-col gap-2 pl-5">
            {block.map((point, j) => (
              <li key={j}>
                {point.label ? (
                  <strong className="font-semibold text-navy-900">
                    {italicise(point.label)}:{' '}
                  </strong>
                ) : null}
                {render(point.parts)}
              </li>
            ))}
          </ul>
        ) : block.kind === 'heading' ? (
          <h3 key={i} className="pt-2 font-serif text-lg font-semibold text-navy-900">
            {block.label}
          </h3>
        ) : (
          <p key={i}>{render(block.parts)}</p>
        ),
      )}
    </div>
  );
}

export function AnswerView({
  answer,
  turnId,
  actions,
}: {
  answer: AskAnswer;
  turnId: number;
  /** Shown under an answered question, e.g. "Save as memo" (ADR-024). */
  actions?: ReactNode;
}) {
  const anchor = (n: number) => `ask-${turnId}-source-${n}`;
  // Sources open in the library; this tells that page how to return to the chat.
  const location = useLocation();
  const backTo = { backTo: `${location.pathname}${location.search}` };
  const prose = answer.segments.map((s) => s.text).join('');
  const local = answer.sources.filter((s) => s.collection !== 'external');
  const external = answer.sources.filter((s) => s.collection === 'external');

  if (answer.outcome === 'declined') {
    return (
      <p role="status" className="rounded-lg border border-line-200 bg-white p-4 text-ink-900">
        Ask Buakei could not answer this question. Try rephrasing it as a question about what an Act
        provides.
      </p>
    );
  }
  if (answer.outcome === 'not_found') {
    return (
      <div className="flex flex-col gap-3">
        <EmptyState title="Not found within the documents searched">
          <p>
            No passage in the {answer.searched.acts} Acts and judgments searched answers this. That
            does not mean the law is silent: the library may not hold the relevant law, or it may
            use different words. Try other terms, or a party's name or a citation for a case.
          </p>
        </EmptyState>
        {prose.trim() ? (
          <p className="text-sm whitespace-pre-line text-muted-600">{prose.trim()}</p>
        ) : null}
      </div>
    );
  }

  return (
    <article className="flex flex-col gap-4">
      <AnswerBody answer={answer} anchor={anchor} />
      {answer.guidedBy ? (
        <p className="rounded-md bg-navy-50 px-3 py-2 text-sm text-ink-900">
          Researched with key points a lawyer approved for this question. This answer itself has not
          been reviewed by a lawyer: check it against the sources below.
        </p>
      ) : null}
      {answer.documentsRead?.length ? (
        <section
          aria-label="Your documents read"
          className="flex flex-col gap-1 border-t border-line-200 pt-4 text-sm"
        >
          <h3 className="font-semibold text-navy-900">Your documents read</h3>
          <ul className="flex flex-col gap-0.5 text-ink-900">
            {answer.documentsRead.map((d) => (
              <li key={d.name}>
                {d.name} — {d.pages}
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-600">
            Your own material, not legal authority. Check what the answer says about it against the
            document.
          </p>
        </section>
      ) : null}
      {answer.sources.length > 0 ? (
        <section aria-label="Sources" className="flex flex-col gap-3 border-t border-line-200 pt-4">
          <h3 className="text-sm font-semibold text-navy-900">
            {external.length > 0 ? 'Sierra Leone sources' : 'Sources'}
          </h3>
          <ol className="flex flex-col gap-3">
            {local.map((s) => (
              <li key={s.n} id={anchor(s.n)} className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-navy-900">
                  [{s.n}] {displayTitle(s.title)} — {s.label}
                </span>
                {s.quotes.map((q) => (
                  <blockquote
                    key={q}
                    className="border-l-2 border-navy-900 pl-3 font-serif text-ink-900"
                  >
                    “{q}”
                  </blockquote>
                ))}
                <Link
                  to={`/library/${s.collection === 'cases' ? 'cases/' : ''}${s.versionId}?page=${s.pageStart}`}
                  state={backTo}
                  className="flex items-center gap-1 self-start font-medium text-navy-900 underline"
                >
                  <FileText className="size-4" aria-hidden /> Open the original at{' '}
                  {pages(s.pageStart, s.pageEnd)}
                </Link>
                <ReportProblem
                  versionId={s.versionId}
                  passageId={s.passageId}
                  context="answer"
                  label="Report a problem with this source"
                />
              </li>
            ))}
          </ol>
        </section>
      ) : null}
      {external.length > 0 ? (
        <section
          aria-label="External authorities"
          className="flex flex-col gap-3 border-t border-line-200 pt-4"
        >
          <h3 className="text-sm font-semibold text-navy-900">
            External authorities: not Sierra Leone law
          </h3>
          <p className="text-xs text-muted-600">
            Found in approved external sources. Each is persuasive or secondary unless its status
            says otherwise; none is binding in Sierra Leone.
          </p>
          <ol className="flex flex-col gap-3">
            {external.map((s) => (
              <ExternalSourceItem key={s.n} s={s} id={anchor(s.n)} />
            ))}
          </ol>
        </section>
      ) : null}
      {answer.externalResearch?.length ? (
        <ExternalResearchLogView logs={answer.externalResearch} />
      ) : null}
      <p className="text-xs text-muted-600">
        Quotes come from text extracted automatically, which may contain errors; check each against
        the original. Legal information, not legal advice.
      </p>
      {actions}
    </article>
  );
}

/** Today, Yesterday, then "This week" and "Earlier", for scanning the drawer. */
function recency(iso: string, now: Date): string {
  const day = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const days = Math.round((day(now) - day(new Date(iso))) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return 'This week';
  return 'Earlier';
}

/** The account's past chats (ADR-019), in the history drawer: open, start new, delete. */
function ChatHistory({
  tokens,
  current,
  onOpen,
  onNew,
  disabled,
}: {
  tokens: IdentityClient;
  current: string | null;
  onOpen: (id: string) => void;
  onNew: () => void;
  disabled: boolean;
}) {
  const queryClient = useQueryClient();
  const searchId = useId();
  const list = useQuery({
    queryKey: ['assistant', 'chats'],
    queryFn: () => assistantApi.chats(tokens),
  });
  const [filter, setFilter] = useState('');
  const [confirmAll, setConfirmAll] = useState(false);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['assistant', 'chats'] });
  const removeOne = useMutation({
    mutationFn: (id: string) => assistantApi.deleteChat(tokens, id),
    onSuccess: (_result, id) => {
      if (id === current) onNew();
      refresh();
    },
  });
  const removeAll = useMutation({
    mutationFn: () => assistantApi.deleteAllChats(tokens),
    onSuccess: () => {
      setConfirmAll(false);
      onNew();
      refresh();
    },
  });

  const now = new Date();
  const term = filter.trim().toLowerCase();
  const chats = (list.data?.chats ?? []).filter(
    (c) => !term || c.title.toLowerCase().includes(term),
  );
  const groups: { label: string; chats: typeof chats }[] = [];
  for (const chat of chats) {
    const label = recency(chat.updatedAt, now);
    const group = groups.find((g) => g.label === label);
    if (group) group.chats.push(chat);
    else groups.push({ label, chats: [chat] });
  }

  return (
    <nav aria-label="Chat history" className="flex min-h-0 flex-1 flex-col gap-3 p-4">
      <Button onClick={onNew} disabled={disabled}>
        <Plus aria-hidden /> New chat
      </Button>
      {(list.data?.chats.length ?? 0) > 5 ? (
        <div className="flex flex-col gap-1">
          <label htmlFor={searchId} className="sr-only">
            Search your chats
          </label>
          <input
            id={searchId}
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search chats…"
            className="h-9 rounded-md border border-line-200 bg-white px-3 text-sm text-ink-900 placeholder:text-muted-600"
          />
        </div>
      ) : null}
      {list.isPending ? <LoadingState label="Loading your chats" /> : null}
      {list.isError ? (
        <p role="alert" className="text-sm text-ink-900">
          Your chats could not be loaded.
        </p>
      ) : null}
      {list.data && chats.length === 0 ? (
        <p className="text-sm text-muted-600">
          {term ? 'No chats match.' : 'Your chats will appear here.'}
        </p>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
        {groups.map((group) => (
          <section key={group.label} aria-label={group.label} className="flex flex-col gap-1">
            <h3 className="px-2 text-xs font-medium text-muted-600">{group.label}</h3>
            <ul className="flex flex-col">
              {group.chats.map((chat) => {
                const selected = chat.id === current;
                return (
                  <li key={chat.id} className="flex items-start gap-1">
                    <button
                      type="button"
                      onClick={() => onOpen(chat.id)}
                      disabled={disabled}
                      title={chat.title}
                      aria-current={selected ? 'true' : undefined}
                      className={cn(
                        'flex min-w-0 flex-1 flex-col gap-0.5 rounded-md border-l-2 px-2 py-1.5 text-left text-sm',
                        selected
                          ? 'border-navy-900 bg-navy-50 text-navy-900'
                          : 'border-transparent text-ink-900 hover:bg-navy-50',
                      )}
                    >
                      <span className="line-clamp-2 font-medium">{chat.title}</span>
                      <span className="text-xs text-muted-600">
                        {new Date(chat.updatedAt).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                        })}
                      </span>
                    </button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8 shrink-0 text-muted-600"
                      aria-label={`Delete chat: ${chat.title}`}
                      disabled={removeOne.isPending}
                      onClick={() => removeOne.mutate(chat.id)}
                    >
                      <Trash2 aria-hidden />
                    </Button>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>

      {(list.data?.chats.length ?? 0) > 0 ? (
        <div className="border-t border-line-200 pt-3">
          {confirmAll ? (
            <div
              role="alertdialog"
              aria-label="Delete all chats"
              className="flex flex-col gap-2 text-sm"
            >
              <p className="text-ink-900">
                Delete all your chats permanently? They cannot be recovered.
              </p>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => removeAll.mutate()} disabled={removeAll.isPending}>
                  Delete all
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setConfirmAll(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmAll(true)}
              className="flex items-center gap-2 text-sm text-muted-600 underline hover:text-navy-900"
            >
              <Trash2 className="size-4" aria-hidden /> Delete all chats
            </button>
          )}
        </div>
      ) : null}
    </nav>
  );
}

/** The full explanation behind the one-line notice by the composer. */
function HowAnswersWork() {
  return (
    <div className="flex flex-col gap-3 p-4 text-sm leading-relaxed text-ink-900">
      <p>
        Answers come only from the Acts and judgments in the Buakei AI library: the Acts of Sierra
        Leone, and judgments of the Supreme Court, Court of Appeal and High Court published by
        SierraLII. A question the library cannot answer is reported as not found within the
        documents searched, which does not mean the law is silent.
      </p>
      <p>
        Every answer lists numbered sources. Each one shows the quoted text and opens the original
        document at the cited page. The text of the documents was extracted automatically, often by
        OCR, and has not been verified: check every source against the original before relying on
        it. Answers are legal information, not legal advice.
      </p>
      <p>
        Your question and the passages found are sent to Anthropic's Claude to prepare the answer.
        Your chats are kept, visible only to you, until you delete them; deleting a chat or all
        chats is permanent. Do not enter client-confidential information.
      </p>
    </div>
  );
}

/**
 * Ask Buakei workspace (ADR-017, ADR-019): a slim toolbar, the conversation in a centred
 * reading column, and the composer at the bottom; history opens in a drawer over it. With
 * `fill`, it fills the space below the app header (the dedicated page); otherwise it takes a
 * viewport-based height inside another page.
 */
export function ActsAssistant({
  tokens,
  fill = false,
  back,
  saveTo,
  documentsIn,
}: {
  tokens: IdentityClient;
  fill?: boolean;
  back?: { to: string; label: string };
  /** Offer "Save as memo" into this Professional workspace (ADR-024). */
  saveTo?: { id: string; name: string; kind: string };
  /** Let questions read the user's documents in this Professional workspace (ADR-045). */
  documentsIn?: string;
}) {
  const inputId = useId();
  const hintId = useId();
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const [documents, setDocuments] = useState<string[]>([]);
  const [documentsOpen, setDocumentsOpen] = useState(false);
  const documentsPanelId = useId();
  const documentsButtonRef = useRef<HTMLButtonElement>(null);
  const uploads = useDocumentUploads();
  const [chatId, setChatId] = useState<string | null>(null);
  const [chatTitle, setChatTitle] = useState<string | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [answers, setAnswers] = useState<Record<number, AskAnswer>>({});
  const [remaining, setRemaining] = useState<number | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const historyButtonRef = useRef<HTMLButtonElement>(null);
  // History sits in the app header, beside Feedback, so it stays in view (owner request).
  const headerSlot = useHeaderSlot();
  const aboutButtonRef = useRef<HTMLButtonElement>(null);
  const conversationRef = useRef<HTMLDivElement>(null);
  /**
   * Which conversation is on screen. An answer that arrives after the user opened another
   * chat stays with the chat that asked (it is saved there) and is not shown in this one.
   */
  const view = useRef(0);
  // The open chat lives in the address (?chat=…), so Back from a source returns to it.
  const [params, setParams] = useSearchParams();
  const rememberChat = (id: string | null) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (id) next.set('chat', id);
        else next.delete('chat');
        return next;
      },
      { replace: true },
    );

  const mutation = useMutation({
    mutationFn: (turn: Turn & { view: number }) =>
      assistantApi.ask(
        tokens,
        turn.question,
        turn.chatId ?? undefined,
        documentsIn && turn.documents?.length
          ? { workspaceId: documentsIn, ids: turn.documents }
          : undefined,
      ),
    onSuccess: (answer, turn) => {
      setRemaining(answer.remainingToday);
      void queryClient.invalidateQueries({ queryKey: ['assistant', 'chats'] });
      if (turn.view !== view.current) return;
      setAnswers((all) => ({ ...all, [turn.id]: answer }));
      if (answer.chatId) {
        setChatId(answer.chatId);
        rememberChat(answer.chatId);
        setChatTitle((title) => title ?? turn.question);
      }
    },
  });
  const opening = useMutation({
    mutationFn: (id: string) => assistantApi.chat(tokens, id),
    onSuccess: (chat) => {
      view.current += 1;
      setChatId(chat.id);
      setChatTitle(chat.title);
      rememberChat(chat.id);
      setTurns(chat.history.map((t, i) => ({ id: i + 1, question: t.question, chatId: chat.id })));
      setAnswers(Object.fromEntries(chat.history.map((t, i) => [i + 1, t.answer])));
      // Show the conversation from its start, with focus in it, after it renders.
      requestAnimationFrame(() => {
        conversationRef.current?.scrollTo({ top: 0 });
        conversationRef.current?.focus();
      });
    },
  });

  // Returning to the page (e.g. Back from a source): reopen the chat named in the address.
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    const fromAddress = params.get('chat');
    if (fromAddress) opening.mutate(fromAddress);
  }, [params, opening]);

  const startNew = () => {
    view.current += 1;
    setChatId(null);
    setChatTitle(null);
    rememberChat(null);
    setTurns([]);
    setAnswers({});
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const question = text.trim();
    if (question.length < 3 || mutation.isPending) return;
    const turn = { id: Date.now(), question, chatId, documents, view: view.current };
    setTurns((all) => [...all, turn]);
    setText('');
    mutation.mutate(turn);
    // Bring the new question into view; the answer then appears below without moving the page.
    requestAnimationFrame(() =>
      document.getElementById(`ask-turn-${turn.id}`)?.scrollIntoView({ block: 'start' }),
    );
  };

  const pending =
    mutation.isPending && mutation.variables.view === view.current ? mutation.variables : undefined;
  const failed =
    mutation.isError && mutation.variables.view === view.current ? mutation.variables : undefined;
  const busy = mutation.isPending || opening.isPending;
  const empty = turns.length === 0 && !opening.isPending;

  return (
    <section
      aria-labelledby={`${inputId}-heading`}
      className={cn(
        'flex min-h-0 flex-col',
        fill ? 'h-full' : 'h-[calc(100vh-16rem)] h-[calc(100dvh-16rem)] min-h-[28rem]',
      )}
    >
      <Sheet
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        title="Chat history"
        side="left"
        className="flex flex-col"
        returnFocusRef={historyButtonRef}
      >
        <ChatHistory
          tokens={tokens}
          current={chatId}
          onOpen={(id) => {
            setHistoryOpen(false);
            opening.mutate(id);
          }}
          onNew={() => {
            setHistoryOpen(false);
            startNew();
          }}
          disabled={opening.isPending}
        />
      </Sheet>
      <Sheet
        open={aboutOpen}
        onOpenChange={setAboutOpen}
        title="How answers work"
        side="right"
        returnFocusRef={aboutButtonRef}
      >
        <HowAnswersWork />
      </Sheet>

      {headerSlot
        ? createPortal(
            <Button
              ref={historyButtonRef}
              variant="ghost"
              size="sm"
              onClick={() => setHistoryOpen(true)}
            >
              <History aria-hidden />
              <span className="sr-only sm:not-sr-only">History</span>
            </Button>,
            headerSlot,
          )
        : null}

      {/* Slim toolbar: the conversation's name, new chat, and the way back. */}
      <div className="flex shrink-0 items-center gap-2 border-b border-line-200 px-1 pb-2">
        {headerSlot ? null : (
          <Button
            ref={historyButtonRef}
            variant="secondary"
            size="sm"
            onClick={() => setHistoryOpen(true)}
          >
            <History aria-hidden /> History
          </Button>
        )}
        <h2
          id={`${inputId}-heading`}
          className="min-w-0 flex-1 truncate px-2 text-sm font-medium text-navy-900"
          title={chatTitle ?? undefined}
        >
          {chatTitle ?? 'Ask Buakei'}
        </h2>
        <Button variant="secondary" size="sm" onClick={startNew} disabled={opening.isPending}>
          <Plus aria-hidden /> New chat
        </Button>
        {back ? (
          <Link
            to={back.to}
            className="hidden items-center gap-1 px-2 text-sm font-medium text-navy-900 underline sm:flex"
          >
            <ArrowLeft className="size-4" aria-hidden /> {back.label}
          </Link>
        ) : null}
      </div>

      {/* The conversation scrolls on its own; nothing moves it while an answer is read. */}
      <div
        ref={conversationRef}
        tabIndex={-1}
        role="region"
        aria-label="Conversation"
        className="min-h-0 flex-1 overflow-y-auto focus:outline-none"
      >
        <div className="mx-auto flex w-full max-w-[56rem] flex-col gap-8 px-1 py-6">
          {empty ? (
            <div className="flex flex-col gap-2 py-10 text-center">
              <p className="font-serif text-2xl font-semibold text-navy-900">
                Ask about the law of Sierra Leone
              </p>
              <p className="text-muted-600">
                Answers draw on the Acts and judgments in the library, with sources you can open.
              </p>
            </div>
          ) : null}
          {opening.isPending ? <LoadingState label="Opening the chat" /> : null}
          {opening.isError ? (
            <ErrorState title="The chat could not be opened" onRetry={() => opening.reset()}>
              {opening.error.message}
            </ErrorState>
          ) : null}
          <div className="flex flex-col gap-8" aria-live="polite">
            {turns.map((turn, index) => (
              <div
                key={turn.id}
                id={`ask-turn-${turn.id}`}
                className="flex scroll-mt-4 flex-col gap-4"
              >
                <p className="self-end rounded-lg bg-navy-900 px-4 py-2 text-white md:max-w-[80%]">
                  <span className="sr-only">You asked: </span>
                  {turn.question}
                </p>
                {answers[turn.id] ? (
                  <AnswerView
                    answer={answers[turn.id]!}
                    turnId={turn.id}
                    actions={
                      saveTo && chatId && answers[turn.id]!.outcome === 'answered' ? (
                        <SaveMemo
                          tokens={tokens}
                          workspace={saveTo}
                          chatId={chatId}
                          // Its position in the saved chat: questions that failed were not saved.
                          turn={turns.slice(0, index).filter((t) => answers[t.id]).length}
                          question={turn.question}
                        />
                      ) : undefined
                    }
                  />
                ) : null}
                {pending?.id === turn.id ? (
                  <LoadingState label="Researching the Acts and judgments" />
                ) : null}
                {failed?.id === turn.id ? (
                  <ErrorState
                    title="Ask Buakei could not answer"
                    onRetry={() => mutation.mutate({ ...turn, view: view.current })}
                  >
                    {mutation.error?.message}
                  </ErrorState>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Composer, aligned with the reading column and always reachable. */}
      <div className="shrink-0 border-t border-line-200 px-1 pt-3">
        <form
          onSubmit={onSubmit}
          className="relative mx-auto flex w-full max-w-[56rem] flex-col gap-2"
        >
          {documentsIn && documentsOpen ? (
            <QuestionDocumentsPanel
              id={documentsPanelId}
              tokens={tokens}
              workspaceId={documentsIn}
              selected={documents}
              onChange={setDocuments}
              onClose={() => {
                setDocumentsOpen(false);
                documentsButtonRef.current?.focus();
              }}
            />
          ) : null}
          <label htmlFor={inputId} className="sr-only">
            {chatId ? 'Ask a follow-up question' : 'Your question'}
          </label>
          <div className="flex flex-col gap-1 rounded-2xl border border-line-200 bg-white p-2 shadow-sm focus-within:border-navy-900">
            {documentsIn ? (
              <QuestionDocumentChips
                tokens={tokens}
                workspaceId={documentsIn}
                selected={documents}
                onChange={setDocuments}
              />
            ) : null}
            <textarea
              id={inputId}
              aria-describedby={hintId}
              value={text}
              maxLength={QUESTION_MAX}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) onSubmit(e);
              }}
              rows={2}
              placeholder={
                chatId
                  ? 'Ask a follow-up question…'
                  : 'Ask a question about the law of Sierra Leone…'
              }
              className="max-h-40 min-h-[2.75rem] w-full resize-none border-0 bg-transparent px-2 py-1 text-ink-900 placeholder:text-muted-600 focus:outline-none"
            />
            <div className="flex items-center justify-between gap-2">
              {documentsIn && uploads.enabled ? (
                <button
                  ref={documentsButtonRef}
                  type="button"
                  aria-label="Add documents"
                  title="Add documents"
                  aria-expanded={documentsOpen}
                  aria-controls={documentsOpen ? documentsPanelId : undefined}
                  onClick={() => setDocumentsOpen((o) => !o)}
                  className="inline-flex size-9 items-center justify-center rounded-lg text-navy-900 hover:bg-navy-50 focus-visible:outline-2 focus-visible:outline-navy-900"
                >
                  <Plus className="size-5" aria-hidden />
                </button>
              ) : (
                <span />
              )}
              <Button type="submit" disabled={text.trim().length < 3 || busy} aria-label="Ask">
                <Send aria-hidden />
                <span className="sr-only sm:not-sr-only">Ask</span>
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 pb-1 text-xs text-muted-600">
            <p id={hintId}>
              Source text is unverified. Check the originals. Do not enter client-confidential
              information.
              {remaining !== null
                ? ` ${remaining} ${remaining === 1 ? 'question' : 'questions'} left today.`
                : ''}
            </p>
            <button
              ref={aboutButtonRef}
              type="button"
              onClick={() => setAboutOpen(true)}
              className="font-medium text-navy-900 underline"
            >
              How answers work
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
